import React, { useState, useEffect } from 'react';
import { User, LogOut } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const Settings = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    role: 'User'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load real user data from Supabase auth
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error) {
        console.error('Failed to get user:', error);
        toast.error('Failed to load user profile');
        return;
      }

      if (user) {
        setProfile({
          name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
          email: user.email || '',
          role: 'User'
        });
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      toast.error('Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = () => {
    toast.success('Profile updated successfully!');
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      try {
        const { error } = await supabase.auth.signOut();
        
        if (error) {
          console.error('Logout error:', error);
          toast.error('Failed to logout: ' + error.message);
          return;
        }

        // Clear any stored data (if any)
        
        toast.success('Logged out successfully');
        navigate('/login');
      } catch (error) {
        console.error('Logout error:', error);
        toast.error('Failed to logout');
      }
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto" data-testid="settings-page">
      <h1 className="text-3xl font-bold mb-8">Settings</h1>

      <div className="space-y-6">
        {/* Profile Settings */}
        <div className="glass-card p-6" data-testid="profile-section">
          <h2 className="text-xl font-semibold mb-4">Profile Settings</h2>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="loading-dots mb-4">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <p className="text-gray-400">Loading profile...</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-6 mb-6">
                <div className="relative group">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <User size={40} />
                  </div>
                  <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                    <span className="text-xs">Change</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{profile.name}</h3>
                  <p className="text-sm text-gray-400">{profile.role}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    className="w-full glass-input"
                    data-testid="profile-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={profile.email}
                    readOnly
                    className="w-full glass-input bg-white/5"
                    data-testid="profile-email-input"
                  />
                  <p className="text-xs text-gray-400 mt-1">Email cannot be changed</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Role</label>
                  <input
                    type="text"
                    value={profile.role}
                    readOnly
                    className="w-full glass-input bg-white/5"
                    data-testid="profile-role-input"
                  />
                </div>

                <button
                  onClick={handleSaveProfile}
                  className="btn-primary"
                  data-testid="save-profile-btn"
                >
                  Save Changes
                </button>
              </div>
            </>
          )}
        </div>

        {/* Preferences */}
        <div className="glass-card p-6" data-testid="preferences-section">
          <h2 className="text-xl font-semibold mb-4">Preferences</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Theme</label>
              <select className="w-full glass-input cursor-pointer" disabled data-testid="theme-select">
                <option value="dark">Dark (Default)</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">Light theme coming soon</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Notifications</label>
              <div className="glass-card p-4 text-center text-gray-400 text-sm">
                Notification settings coming soon
              </div>
            </div>
          </div>
        </div>

        {/* About */}
        <div className="glass-card p-6" data-testid="about-section">
          <h2 className="text-xl font-semibold mb-4">About</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-400">Version</span>
              <span className="font-medium">1.0.0</span>
            </div>
            <div>
              <p className="text-sm text-gray-400">
                Fynex is an R&D Knowledge Management System designed to track problems, provide AI-powered insights, and manage technical documentation.
              </p>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="mt-6 w-full btn-secondary text-red-400 hover:bg-red-500/10 flex items-center justify-center gap-2"
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
